import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { l, tsxl } from 'client/Other/Translation'
import * as React from 'react'
import { useEffect, useState } from 'react'

import {
  DiscountLocation,
  DiscountSettingsInterface,
  DiscountType,
  DiscountVAT,
} from '../../../Interfaces/DiscountSettingsInterface'
import { GlobalEvents } from '../../../Other/GlobalEvents'
import CheckBox from '../../common/CheckBox/CheckBox'
import { TestableInterface } from '../../common/interfaces/TestableInterface'
import { ToggleInput } from '../../common/ToggleInput/ToggleInput'
import './DiscountSettings.scss'

export interface DiscountSettingsProps extends DiscountSettingsInterface, TestableInterface {
  discountLocationsMap: any
  blockedXrechung?: boolean
  discountLocationValue?: string
  discountVATValue?: string
  onDiscountStateChange: (value) => void
  onDiscountLocationChange: (value) => void
  onDiscountTypeChange: (value) => void
  onDiscountVATChange: (value) => void
}

export interface DiscountSettingsState {
  menuOpen: boolean
}

export const DiscountSettings = (props: DiscountSettingsProps) => {
  const {
    discountLocationsMap,
    blockedXrechung,
    discountLocationValue,
    discountVATValue,
    discountLocation,
    discountState,
    discountType,
    currency,
    automation,
    showDiscountVAT,
    discountVAT,
    onDiscountStateChange,
    onDiscountLocationChange,
    onDiscountTypeChange,
    onDiscountVATChange,
  } = props

  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const handleOnBlur = () => {
    setMenuOpen(false)
  }

  let e

  useEffect(() => {
    e = GlobalEvents.Click((_e) => {
      handleOnBlur()
    })
    setMenuOpen(props.blockedXrechung)
  }, [])

  useEffect(() => {
    GlobalEvents.unBind(e)
  }, [])

  const getSelectedValues = (): string => {
    const values = []
    if (discountState === 'off') {
      values.push(l('invoice.discount.off'))
    } else {
      if (discountLocation) {
        values.push(l('document.' + discountLocation))
      }
      if (discountType) {
        if (discountType === 'percentage') {
          values.push('%')
        } else {
          values.push(currency)
        }
      }
      if (discountVAT && showDiscountVAT && (discountType === 'amount' || discountType === 'percentage')) {
        if (discountVAT === 'preVAT') {
          values.push(l('invoice.PreVAT'))
        } else {
          values.push(l('invoice.PostVAT'))
        }
      }
    }
    return values.join(', ')
  }

  const discountMap = () => {
    return {
      preVAT: !(blockedXrechung && discountLocationValue === 'perTotal' && discountVATValue === 'preVAT')
        ? l('invoice.PreVAT')
        : l('invoice.preVAT.xrechnung.fail'),
      postVAT: !(blockedXrechung && discountLocationValue === 'perLine' && discountVATValue === 'postVAT')
        ? l('invoice.PostVAT')
        : l('invoice.postVAT.xrechnung.fail'),
    }
  }
  const invalidDiscountGroup = () => {
    if (blockedXrechung && discountLocationValue === 'perTotal' && discountVATValue === 'preVAT') {
      return ['preVAT', 'perTotal']
    } else if (blockedXrechung && discountLocationValue === 'perLine' && discountVATValue === 'postVAT') {
      return ['postVAT', 'perLine']
    }
  }

  const discountButtons = []
  if (menuOpen) {
    if (discountState === 'on') {
      discountButtons.push(
        <ToggleInput
          key="2"
          radio={true}
          name="discountLocationToggler"
          automation={automation + '-discountLocation'}
          label={tsxl('invoice.discountLocation')}
          value={discountLocation}
          map={discountLocationsMap}
          onChange={(discountLocation: DiscountLocation) => {
            if (discountLocation === 'perLine') {
              Segment.track(SegmentEvents.editorSettings.discount.perLineToggleClicked)
            }
            if (discountLocation === 'perTotal') {
              Segment.track(SegmentEvents.editorSettings.discount.perTotalToggleClicked)
            }
            onDiscountLocationChange(discountLocation)
          }}
          invalidItems={invalidDiscountGroup()}
        />,
        <hr key="5" />,
        <ToggleInput
          key="3"
          radio={true}
          name="discountTypeToggler"
          automation={automation + '-discountType'}
          label={tsxl('invoice.discountType')}
          value={discountType}
          map={{
            percentage: l('invoice.percentage'),
            amount: currency,
          }}
          onChange={(discountType: DiscountType) => {
            if (discountType === 'percentage') {
              Segment.track(SegmentEvents.editorSettings.discount.percentageToggleClicked)
            }
            if (discountType === 'amount') {
              Segment.track(SegmentEvents.editorSettings.discount.currencyToggleClicked)
            }
            onDiscountTypeChange(discountType)
          }}
        />,
      )
      if (showDiscountVAT) {
        discountButtons.push(
          <hr key="6" />,
          <ToggleInput
            key="4"
            radio={true}
            name="discountVATToggler"
            automation={automation + '-discountVAT'}
            label={tsxl('invoice.DiscountVAT')}
            value={discountVAT}
            map={discountMap()}
            onChange={(discountVAT: DiscountVAT) => {
              if (discountVAT === 'preVAT') {
                Segment.track(SegmentEvents.editorSettings.discount.excludingVatToggleClicked)
              }
              if (discountVAT === 'postVAT') {
                Segment.track(SegmentEvents.editorSettings.discount.includingVatToggleClicked)
              }
              onDiscountVATChange(discountVAT)
            }}
            invalidItems={invalidDiscountGroup()}
          />,
        )
      }
    }
  }

  let toggles = null
  const classes = ['DiscountSettings', 'Controlled', 'Input']

  if (menuOpen) {
    toggles = (
      <div className="toggles" data-automation={automation + '-discountSettingsToggles'}>
        {discountButtons}
      </div>
    )
    classes.push('menuOpen')
  }

  const discountContent: JSX.Element = (
    <div
      className={classes.join(' ')}
      data-automation={automation}
      onClick={(e) => {
        GlobalEvents.prevent(e)
        return false
      }}
    >
      <CheckBox
        value={discountState === 'on'}
        label={tsxl('invoice.discount')}
        automation={automation + '-discountState'}
        onChange={(value) => {
          Segment.track(SegmentEvents.editorSettings.discount.discountCheckboxClicked)
          onDiscountStateChange(value ? 'on' : 'off')
        }}
      />
      {discountState === 'on' && (
        <div
          className="values Controlled"
          data-automation={automation + '-value'}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className="values">{getSelectedValues()}</span>
          <span className="arrow" />
        </div>
      )}
      {discountState === 'on' && toggles}
    </div>
  )
  return discountContent
}
